import { toBetterTimeString } from "../../utils";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import React, { useCallback, useEffect, useState } from "react";
import {
  Board,
  COMPONENT_IDS,
  DeviceMetadata,
  endOfLineTestResultDataIsCompatible,
  firstTimeSetupStartedDataIsCompatible,
  KeyValueProperty,
  LifecycleEventMap,
  LifecycleEventMapDataType,
  LifecycleEventMapResponse,
  softwareUpdateRequestedDataIsCompatible,
} from "../../models";
import { HTTP_CLIENT } from "../../hooks";
import { toast } from "react-toastify";

interface BoardProps {
  jwt: string;
  board: Board;
  expanded: boolean;
  onClick: (deviceUid: string) => void;
  setBoardToEdit: (board: Board) => void;
  setShowAddEditModal: (show: boolean) => void;
  setBoardToDelete: (board: Board) => void;
  setShowDeleteConfirmationModal: (show: boolean) => void;
  events: Map<number, KeyValueProperty>;
}

function compute_pin(serial: string): string {
  // First 2 of pin, 1 for the station ID, 5 for the index
  const min_char_length = 2 + 1 + 5;

  serial = serial.trim();
  const end_of_serial = serial.substring(serial.length - min_char_length);
  return (
    end_of_serial.substring(0, 2) +
    end_of_serial.substring(end_of_serial.length - 4)
  );
}

const EVENT_MAPS_PATH_PREFIX = "/api/v1/boardLifecycleEventMaps";

function LifecycleEventDataCell({
  event,
  data,
}: Readonly<{
  event: number;
  data: LifecycleEventMapDataType;
}>) {
  const columns = 8;
  if (!data) {
    return <td colSpan={columns} />;
  } else {
    switch (event) {
      case 2: {
        const results = data.data;
        if (endOfLineTestResultDataIsCompatible(data.version, results)) {
          return (
            <td colSpan={columns}>
              <strong>{results.result}</strong>{" "}
              {results.client_metadata.ble_rssi
                ? `${results.client_metadata.ble_rssi}dBm`
                : ""}{" "}
              ({results.test_runner})
            </td>
          );
        } else {
          return <td colSpan={columns}>Unsupported data model</td>;
        }
      }
      case 7: {
        const results = data.data;
        if (softwareUpdateRequestedDataIsCompatible(data.version, results)) {
          return (
            <td colSpan={columns}>
              Target:{" "}
              <strong>
                {COMPONENT_IDS[`${results.target_component}`] || "Unknown"}
              </strong>
              , Current: {componentStack(results.device_metadata)}
            </td>
          );
        } else {
          return <td colSpan={columns}>Unsupported data model</td>;
        }
      }
      case 13: {
        const results = data.data;
        if (firstTimeSetupStartedDataIsCompatible(data.version, results)) {
          return (
            <td colSpan={columns}>{componentStack(results.device_metadata)}</td>
          );
        } else {
          return <td colSpan={columns}>Unsupported data model</td>;
        }
      }
      default: {
        return (
          <td colSpan={columns}>
            {componentStack(data?.data?.device_metadata)}
          </td>
        );
      }
    }
  }
}

function componentStack(
  device_metadata: undefined | null | DeviceMetadata,
): string {
  if (device_metadata?.versions) {
    const versions = device_metadata.versions;
    return `fw=${versions["2"]}, bl=${versions["4"]}, fido=${versions["3"]}`;
  } else {
    return "";
  }
}

export function BoardRow({
  jwt,
  board,
  expanded,
  onClick,
  setBoardToEdit,
  setShowAddEditModal,
  setBoardToDelete,
  setShowDeleteConfirmationModal,
  events,
}: Readonly<BoardProps>) {
  const [eventMaps, setEventMaps] = useState<LifecycleEventMap[]>([]);

  const updateEventMapsList = useCallback(async () => {
    if (expanded) {
      try {
        const response = await HTTP_CLIENT.get<LifecycleEventMapResponse>({
          path: `${EVENT_MAPS_PATH_PREFIX}/search/findAllByDeviceUidOrderByTimestampDesc`,
          query: { deviceUid: board.device_uid },
          headers: { Authorization: `Bearer ${jwt}` },
        });
        setEventMaps(response._embedded.boardLifecycleEventMaps);
      } catch (e: any) {
        console.error(e.stack);
        toast.error(
          `Failed to retrieve lifecycle event maps for ${board.device_uid} due to:\n${e.message}`,
        );
      }
    } else {
      setEventMaps([]);
    }
  }, [expanded, jwt, board, setEventMaps]);

  useEffect(() => {
    if (jwt) {
      updateEventMapsList().catch((e) => toast.error(e));
    }
  }, [updateEventMapsList, jwt]);

  return (
    <>
      <tr key={`board-${board.device_uid}`}>
        <td
          title={board.model.description || undefined}
          onClick={() => onClick(board.device_uid)}
          style={{ cursor: "pointer" }}
        >
          {board.model.name}
        </td>

        <td
          onClick={() => onClick(board.device_uid)}
          style={{ cursor: "pointer" }}
        >
          {board.device_uid}
        </td>

        <td
          onClick={() => onClick(board.device_uid)}
          style={{ cursor: "pointer" }}
        >
          <code>{board.mac}</code>
        </td>

        <td
          onClick={() => onClick(board.device_uid)}
          style={{ cursor: "pointer" }}
        >
          <code>{board.serial}</code>
        </td>

        <td
          onClick={() => onClick(board.device_uid)}
          style={{ cursor: "pointer" }}
        >
          {board.serial ? <code>{compute_pin(board.serial)}</code> : null}
        </td>

        <td
          onClick={() => onClick(board.device_uid)}
          style={{ cursor: "pointer" }}
        >
          <code>{board.pcb_serial}</code>
        </td>

        <td
          onClick={() => onClick(board.device_uid)}
          style={{ cursor: "pointer" }}
        >
          <code>{board.battery}</code>
        </td>

        <td
          title={board.status.description || undefined}
          onClick={() => onClick(board.device_uid)}
          style={{ cursor: "pointer" }}
        >
          {board.status.name}
        </td>

        <td
          title={board.failure_code?.description || undefined}
          onClick={() => onClick(board.device_uid)}
          style={{ cursor: "pointer" }}
        >
          {board.failure_code?.name}
        </td>

        <td
          onClick={() => onClick(board.device_uid)}
          style={{ cursor: "pointer" }}
        >
          {toBetterTimeString(board.last_updated * 1000)}
        </td>

        <td>
          <Button
            size="sm"
            onClick={() => {
              setBoardToEdit(board);
              setShowAddEditModal(true);
            }}
          >
            Edit
          </Button>
          <Button
            style={{ marginLeft: "1em" }}
            size="sm"
            variant="danger"
            onClick={() => {
              setBoardToDelete(board);
              setShowDeleteConfirmationModal(true);
            }}
          >
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </td>
      </tr>
      {eventMaps.map((e) => {
        const timestamp = new Date(e.timestamp * 1000);
        return (
          <tr key={`board-${board.device_uid}-${e.event}-${e.timestamp}`}>
            <td title={`Event details: ${events.get(e.event)?.description}`}>
              <em>{events.get(e.event)?.name}</em>
            </td>
            <td>Local: {timestamp.toLocaleString()}</td>
            <td>
              KST:{" "}
              {timestamp.toLocaleString(undefined, {
                timeZone: "Asia/Seoul",
              })}
            </td>
            <LifecycleEventDataCell event={e.event} data={e.data} />
          </tr>
        );
      })}
    </>
  );
}
